const CURRENCY = 'JPY'

const toNumber = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const getProductPrice = (product) => {
  const price = product && product.price
  if (price && typeof price === 'object') {
    return toNumber(price.price ?? price.value ?? price.amount)
  }
  return toNumber(price)
}

const getVariationName = (variation) => {
  if (Array.isArray(variation)) {
    return variation
      .map((item) => item?.variation_name ?? item?.name ?? item?.value ?? item)
      .filter(Boolean)
      .join(' / ')
  }
  return variation?.variation_name ?? variation?.name ?? variation?.value ?? variation
}

export const trackEvent = (name, params = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false
  window.gtag('event', name, params)
  return true
}

export const buildGaItem = (product, quantity = 1, variation, overrides = {}) => {
  if (!product) return overrides
  const brand = product.brand
  const category = product.category
  const item = {
    item_id: String(product.id ?? product.product_id ?? overrides.item_id ?? ''),
    item_name: product.product_name ?? product.name ?? overrides.item_name,
    item_brand: brand?.brand_name ?? brand?.name ?? brand ?? overrides.item_brand,
    item_category: category?.category_name ?? category?.name ?? category ?? overrides.item_category,
    item_variant: getVariationName(variation ?? product.variation ?? product.variations),
    price: getProductPrice(product),
    quantity: toNumber(quantity) || 1,
    ...overrides,
  }

  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )
}

export const buildCartItems = (cartItems = []) => cartItems.map((cartItem) =>
  buildGaItem(
    cartItem.product ?? cartItem,
    cartItem.quantite ?? cartItem.quantity ?? 1,
    cartItem.variation ?? cartItem.variations
  )
)

export const calculateItemsValue = (items = []) => items.reduce(
  (total, item) => total + toNumber(item.price) * (toNumber(item.quantity) || 1),
  0
)

export const trackPageView = ({ pathname, search = '' }) => trackEvent('page_view', {
  page_title: document.title,
  page_location: window.location.href,
  page_path: `${pathname}${search}`,
})

export const trackEcommerce = (name, items, params = {}) => {
  const normalizedItems = Array.isArray(items) ? items.filter(Boolean) : []
  return trackEvent(name, {
    currency: CURRENCY,
    value: calculateItemsValue(normalizedItems),
    ...params,
    items: normalizedItems,
  })
}

export const trackPurchase = ({ transactionId, items, value, shipping, tax, coupon, affiliation }) => {
  if (!transactionId || !Array.isArray(items) || items.length === 0) return false

  const storageKey = `ga4_purchase_${transactionId}`
  try {
    if (window.localStorage.getItem(storageKey)) return false
  } catch (error) {
    // Analytics must not interrupt checkout when storage is unavailable.
  }

  const sent = trackEvent('purchase', {
    transaction_id: String(transactionId),
    currency: CURRENCY,
    value: toNumber(value ?? calculateItemsValue(items)),
    shipping: toNumber(shipping),
    tax: toNumber(tax),
    coupon: coupon || undefined,
    affiliation: affiliation || 'Enclopedia',
    items,
  })

  if (sent) {
    try {
      window.localStorage.setItem(storageKey, '1')
    } catch (error) {
      // The event was sent successfully even if deduplication cannot be persisted.
    }
  }
  return sent
}
